import { Link, useNavigate } from "react-router-dom";
import { Table2, BookOpen, ArrowRight, ShieldAlert, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { ConnectSourceDialog } from "@/components/ConnectSourceDialog";
import { InstantDemoHero } from "@/components/InstantDemoHero";
import { AUTH } from "@/constants/testIds";
import { useSession } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";

const CARDS = [
  {
    to: "/schema",
    icon: Table2,
    title: "Browse the schema",
    body: "Every table and column with its semantic definition and sensitivity flag.",
  },
  {
    to: "/glossary",
    icon: BookOpen,
    title: "Open the glossary",
    body: "A living, editable dictionary anyone can search — engineering not required.",
  },
  {
    to: "/glossary",
    icon: ShieldAlert,
    title: "Review PII risk",
    body: "Sensitive columns (SSN, email, DOB, payroll) are auto-flagged on ingest.",
  },
];

export default function Dashboard() {
  const { user } = useSession();
  const { active } = useWorkspace();
  const navigate = useNavigate();

  return (
    <div data-testid={AUTH.dashboardRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-12">
        <p className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
          {user?.role === "admin" ? "admin" : "member"} · authenticated
        </p>

        <h1 data-testid={AUTH.dashboardWelcome} className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">
          Welcome, {user?.name || user?.email}.
        </h1>
        <div aria-hidden="true" className="sema-accent-underline mt-5" />
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          The semantic layer for your data. Start by connecting a source and
          generating the glossary, then explore meanings across your schema.
        </p>

        {/* Instant demo hero — the fastest path from zero to answer */}
        <InstantDemoHero />

        {/* prominent connect CTA */}
        <section data-testid="dashboard-connect-db-cta" className="relative mt-10 overflow-hidden rounded-2xl border border-accent/40 bg-card/60 p-6 backdrop-blur sm:p-8">
          <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-accent">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
                step 1 · get connected
              </p>
              <h2 className="mt-2 text-lg font-semibold">Connect a database</h2>
              <p className="mt-1.5 max-w-lg text-xs leading-relaxed text-muted-foreground">
                PostgreSQL, MySQL, MariaDB, ClickHouse, Redshift — or upload a CSV / XLSX / Parquet file.
                Sema maps your schema, flags PII, and builds the business glossary automatically.
              </p>
            </div>
            <ConnectSourceDialog
              workspaceId={active?.id}
              onConnected={() => navigate("/schema")}
              trigger={
                <Button data-testid="dashboard-connect-db-btn" className="h-12 shrink-0 rounded-full bg-accent px-6 text-xs uppercase tracking-[0.18em] text-accent-foreground hover:bg-accent/90">
                  <Database className="mr-2 h-4 w-4" />
                  Connect a database
                </Button>
              }
            />
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {CARDS.map(({ to, icon: Icon, title, body }) => (
            <Link
              key={title}
              to={to}
              className="group rounded-xl border border-border/60 bg-card/50 p-5 backdrop-blur transition-colors hover:border-accent/50"
            >
              <Icon className="h-5 w-5 text-accent" />
              <h3 className="mt-4 flex items-center gap-1 text-sm font-semibold">
                {title}
                <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{body}</p>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
